/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-audio/bulbaudiocommand
 */

import { Command } from 'ckeditor5';
import type {
  ModelWriter, ModelElement, ModelItem, ModelRange, ModelSelectable,
} from 'ckeditor5';

/**
 * The bulb audio command, the actual insert capabilities of the plugin.
 *
 * @extends Command
 */
export class InsertBulbAudioCommand extends Command {

  /**
   * @inheritDoc
   */
  public override execute(
    blockDetails: { id: any; version: any; },
    placeholderElement: ModelRange | ModelItem,
    insertPosition: ModelSelectable,
  ): void {

    this.editor.model.change((writer: ModelWriter): void => {
      // Check for an insertPosition, this would only happen if we had to transcode the asset and had the
      // placeholder and need to put this exactly where the placeholder was.
      if (insertPosition) {
        this.editor.model.insertContent(InsertBulbAudioCommand.createAudioBlock(writer, blockDetails), insertPosition);
      } else {
        this.editor.model.insertContent(InsertBulbAudioCommand.createAudioBlock(writer, blockDetails));
      }

      CKEBulbData.service.ckeStateService.insertParagraphSubject.next(true);

      if (placeholderElement) {
        writer.remove(placeholderElement);
      }
    });
  }

  /**
   * @inheritDoc
   */
  public override refresh(): void {
    const { model } = this.editor;
    const { selection } = model.document;
    const allowedIn = model.schema.findAllowedParent(selection.getFirstPosition(), 'audio');

    this.isEnabled = allowedIn !== null;
  }

  /**
   * Helper method to create our custom audio block in the editor's model.
   *
   * @param writer ModelWriter instance from editor to update model.
   * @param blockDetails Details about the content block we are uploading.
   * @returns ModelElement created audio block.
   */
  private static createAudioBlock(writer: ModelWriter, blockDetails: { id: any; version: any; }): ModelElement {
    return writer.createElement(
      'audio',
      {
        'bulb-block-id': blockDetails.id,
        'bulb-block-version': blockDetails.version,
      },
    );
  }
}
